import { RefreshCw } from "lucide-react";
import { Button } from "@/pages/interface/components/ui/button";
import type { PushProgress, PushSummary } from "./useSyncEngine";
import type { VendorConfig } from "./vendorConfig";

export interface BatchBarProps {
    selectedCount: number;
    total: number;
    vendor: VendorConfig;
    pushing: boolean;
    progress: PushProgress | null;
    summary: PushSummary | null;
    onClear: () => void;
    onPush: () => void;
}

export function BatchBar({ selectedCount, total, vendor, pushing, progress, summary, onClear, onPush }: BatchBarProps) {
    return (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                    {selectedCount} of {total} selected
                    {selectedCount > 0 && (
                        <>
                            {" · "}
                            <button
                                type="button"
                                onClick={onClear}
                                className="font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                            >
                                Clear
                            </button>
                        </>
                    )}
                </p>
                <Button
                    onClick={onPush}
                    disabled={selectedCount === 0 || pushing}
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    <RefreshCw className={pushing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                    {pushing
                        ? progress
                            ? `Syncing ${progress.completed} of ${progress.total}…`
                            : "Syncing…"
                        : `Sync ${selectedCount} to ${vendor.label}`}
                </Button>
            </div>
            {pushing && progress && (
                <div
                    role="progressbar"
                    aria-label={`Sync progress: ${progress.completed} of ${progress.total}`}
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={progress.completed}
                    className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-200"
                >
                    <div
                        className="h-full rounded-full bg-indigo-600 transition-[width] duration-300"
                        style={{ width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%` }}
                    />
                </div>
            )}
            {!pushing && summary && (
                <p className="mt-2 text-xs text-gray-500">
                    {summary.done} synced · {summary.failed} failed · {summary.skipped} skipped
                </p>
            )}
        </div>
    );
}
